import dayjs from "dayjs";
import { TFunction } from "i18next";
import { AppRoutes } from "../../../../../../common/routesUtils";
import { formatNumber } from "../../../../../../common/utils";
import { PageType } from "../../../../../../config/page.types";
import { FiltersDashboardPayload } from "../../../../../../context/FiltersProvider";
import { FiltersRegistry } from "../../../../../../context/FiltersProvider/types";
import {
	EntityTripDetailsDTO,
	FilterCombinationPayload,
	FilterPayload,
} from "../../../../../../datasources/api-client/data-contracts";

export const displayValues = (
	data: EntityTripDetailsDTO | undefined,
	tFunc: TFunction,
	lang: string,
	appliedFiltersPayload?: FiltersDashboardPayload,
	menuFilters?: FiltersRegistry | null
) => {
	/* -----------------------------
	 * Node filters (data.filters)
	 * ----------------------------- */
	const filters = data?.filters
		? Object.entries(data.filters).map(([key, value]) => ({
				label: key,
				value: String(value ?? "-"),
				measure: "",
				reqExpand: true,
			}))
		: [];

	const transportDataDV = data
		? [
				{
					label: tFunc("tripDetails/id"),
					value: data.id,
					measure: "",
					link: {
						url: AppRoutes.ResultsTransportGreenrouterDetail.replace(":id", data.id),
						pageType: PageType.ResultsTransportGreenRouterDetail,
						id: data.id,
					},
					reqExpand: false,
				},

				{
					label: tFunc(`tripDetails/tripTransportTypeDescription`),
					value: data.tripTransportTypeDescription,
					measure: "",
					reqExpand: false,
				},
				{
					label: tFunc(`tripDetails/weight`),
					value: formatNumber(data.weight.value, "decimal", 2, 2),
					measure: data.weight.measure,
					reqExpand: false,
				},
				{
					label: tFunc(`tripDetails/distance`),
					value: formatNumber(data.distance.value, "decimal", 2, 2),
					measure: data.distance.measure,
					reqExpand: false,
				},
				{
					label: tFunc(`tripDetails/departureAt`),
					value: dayjs(data.departureAt.split(" ")[0]).locale(lang).format("L"),
					measure: "",
					reqExpand: false,
				},
				{
					label: tFunc("tripDetails/tonneKm"),
					value: formatNumber(data.tonneKm.value, "decimal", 2, 2),
					measure: data.tonneKm.measure,
					reqExpand: false,
				},
				{
					label: tFunc(`tripDetails/tripTypeDescription`),
					value: data.tripTypeDescription,
					measure: "",
					reqExpand: true,
				},
				{
					label: tFunc(`tripDetails/description`),
					value: data.description,
					measure: "",
					reqExpand: true,
				},
				{
					label: tFunc(`tripDetails/isIntermodal`),
					value: tFunc(`tripDetails/isIntermodal/${data.isIntermodal}`),
					measure: "",
					reqExpand: true,
				},
				{
					label: tFunc(`tripDetails/goodsTypeDescription`),
					value: data.goodsTypeDescription,
					measure: "",
					reqExpand: true,
				},
				{
					label: tFunc(`tripDetails/temperatureDescription`),
					value: data.temperatureDescription,
					measure: "",
					reqExpand: true,
				},
				...filters,
			]
		: [];

	const entityDataDV = data
		? [
				{
					label: tFunc("tripDetails/weight"),
					value: formatNumber(data.weight.value, "decimal", 2, 2),
					measure: data.weight.measure,
					reqExpand: false,
				},

				{
					label: tFunc("tripDetails/distance"),
					value: formatNumber(data.distance.value, "decimal", 2, 2),
					measure: data.distance.measure,
					reqExpand: false,
				},

				{
					label: tFunc("tripDetails/description"),
					value: data.entityDescription,
					measure: "",
					reqExpand: false,
				},
			]
		: [];

	const transportTotalEmissionsDV = data
		? [
				{
					label: tFunc("tripDetails/totalEmissions"),
					value: formatNumber(data.tCo2eTtw.value, "decimal", 2, 2),
					measure: data.tCo2eTtw.measure,
					reqExpand: false,
				},
				{
					label: tFunc("tripDetails/totalEmissionsWtw"),
					value: formatNumber(data.tCo2eWtw.value, "decimal", 2, 2),
					measure: data.tCo2eWtw.measure,
					reqExpand: false,
				},
				{
					label: tFunc("arcDetails/emission intensity") + " TtW",
					value: formatNumber(data.tCo2eKmTtw.value, "decimal", 2, 2),
					measure: data.tCo2eKmTtw.measure,
					reqExpand: false,
				},
				{
					label: tFunc("arcDetails/emission intensity") + " WtW",
					value: formatNumber(data.tCo2eKmWtw.value, "decimal", 2, 2),
					measure: data.tCo2eKmWtw.measure,
					reqExpand: false,
				},
				{
					label: tFunc(`tripDetails/biogenic emissions`),
					value: formatNumber(data.biogenicEmissions.value, "decimal", 2, 2),
					measure: data.biogenicEmissions.measure,
					reqExpand: false,
				},
				{
					label: tFunc(`tripDetails/hub emissions`),
					value: formatNumber(data.hubEmissions.value, "decimal", 2, 2),
					measure: data.hubEmissions.measure,
					reqExpand: false,
				},
			]
		: [];

	function isFilterPayload(filter: FilterCombinationPayload | FilterPayload): filter is FilterPayload {
		return (filter as FilterPayload).id !== undefined;
	}

	function resolveOptionValue(
		value: string | number | (string | number)[] | undefined,
		options: { id: string | number; label: string }[]
	): string {
		if (value === undefined || value === null) return "";

		const resolve = (v: string | number) => options.find((opt) => opt.id === v)?.label ?? String(v);

		if (Array.isArray(value)) {
			return value.map(resolve).join(", ");
		}

		return resolve(value);
	}

	function normalizeFilters(filters: (FilterCombinationPayload | FilterPayload)[] = []): any[] {
		return filters.flatMap((filter) => {
			if (isFilterPayload(filter)) {
				const menuFilter = menuFilters?.byId[filter.id];

				const operatorLabel =
					menuFilter?.field?.operators.find((op) => op.operator === filter.operator)?.label ??
					filter.operator;

				return [
					{
						label: menuFilter?.name ?? "Unknown Filter",
						value: resolveOptionValue(filter.value, menuFilter?.field?.options ?? []),
						measure: operatorLabel,
						reqExpand: false,
					},
				];
			}

			return normalizeFilters(filter.filters ?? []);
		});
	}

	const appliedFiltersToDetail = normalizeFilters(appliedFiltersPayload?.filters);

	return {
		entityData: entityDataDV,
		transportData: transportDataDV,
		appliedFiltersToDetail,
		transportTotalEmissions: transportTotalEmissionsDV,
	};
};
